import React, { useState } from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * SearchableSelect — accessible combobox with an in-menu search field.
 * options: [{ value, label }]  |  string[]
 * value: currently selected value
 * onChange(value)
 * placeholder / searchPlaceholder / emptyLabel
 * testid: base test id used for the trigger + items
 */
export default function SearchableSelect({
  options, value, onChange,
  placeholder = "Seleccionar…",
  searchPlaceholder = "Buscar…",
  emptyLabel = "Sin resultados",
  className = "",
  testid = "searchable-select",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const opts = (options || []).map(o => typeof o === "string" ? { value: o, label: o } : o);
  const current = opts.find(o => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testid}-trigger`}
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-blue-500 focus:outline-none disabled:opacity-50",
            className
          )}>
          <span className={current ? "" : "text-slate-400"}>{current ? current.label : placeholder}</span>
          <ChevronsUpDown size={16} className="text-slate-400 shrink-0 ml-2"/>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0 z-[60]">
        <Command shouldFilter={true}>
          <CommandInput data-testid={`${testid}-search`} placeholder={searchPlaceholder}/>
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {opts.map(o => (
                <CommandItem
                  key={o.value}
                  value={o.label + " " + o.value}
                  data-testid={`${testid}-item-${o.value}`}
                  onSelect={() => { onChange(o.value); setOpen(false); }}>
                  <Check size={14} className={cn("mr-2", value === o.value ? "opacity-100 text-blue-600" : "opacity-0")}/>
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
